import { Extension } from "@tiptap/core";
import { Node as ProseMirrorNode, Slice } from "@tiptap/pm/model";
import { EditorState, Plugin } from "@tiptap/pm/state";

export interface MaxLengthOptions {
  limit: number;
}

// The text's length in UTF-16 units, as a textarea's maxLength counts it (an emoji is two), each
// line break and each break between blocks counting as one.
export const textLength = (doc: ProseMirrorNode) =>
  doc.textBetween(0, doc.content.size, "\n", "\n").length;

// Within the limit, or no longer than before: a value set from outside can still be shortened.
const allowed = (next: ProseMirrorNode, state: EditorState, limit: number) => {
  const length = textLength(next);
  return length <= limit || length <= textLength(state.doc);
};

// The slice up to `end`, open on the right as deep as its cut leaves it.
const cutSlice = (slice: Slice, end: number) => {
  const content = slice.content.cut(0, end);
  return new Slice(content, slice.openStart, Slice.maxOpen(content).openEnd);
};

// Refuses edits past the limit, and pastes only as much as fits, as a textarea does.
export const MaxLength = Extension.create<MaxLengthOptions>({
  name: "maxLength",

  addOptions() {
    return { limit: Infinity };
  },

  addProseMirrorPlugins() {
    const { limit } = this.options;
    return [
      new Plugin({
        filterTransaction: (transaction, state) =>
          !transaction.docChanged ||
          !!transaction.getMeta("preventUpdate") ||
          allowed(transaction.doc, state, limit),
        props: {
          handlePaste: (view, _event, slice) => {
            const { state } = view;
            const fits = (candidate: Slice) => {
              try {
                return allowed(
                  state.tr.replaceSelection(candidate).doc,
                  state,
                  limit
                );
              } catch {
                return false;
              }
            };
            if (fits(slice)) return false;
            // The paste may stop after any of its characters, never in bare structure such as an
            // empty item, so each stop holds one more character and the longest that fits is found.
            const stops: number[] = [];
            slice.content.descendants((node, pos) => {
              const text = node.text ?? "";
              for (let offset = 1; offset <= text.length; offset++) {
                // Never between the two halves of a surrogate pair, such as an emoji.
                const code = text.charCodeAt(offset - 1);
                if (code >= 0xd800 && code <= 0xdbff) continue;
                stops.push(pos + offset);
              }
            });
            let low = -1;
            let high = stops.length - 1;
            while (low < high) {
              const middle = Math.ceil((low + high) / 2);
              if (fits(cutSlice(slice, stops[middle]))) low = middle;
              else high = middle - 1;
            }
            if (low >= 0) {
              view.dispatch(
                state.tr
                  .replaceSelection(cutSlice(slice, stops[low]))
                  .scrollIntoView()
                  .setMeta("paste", true)
                  .setMeta("uiEvent", "paste")
              );
            }
            return true;
          }
        }
      })
    ];
  }
});
